// MainApp.jsx - Main application with Builder/Analytics navigation
import React, { useEffect, useState, Suspense, lazy } from 'react';
import { useAuth } from '../contexts/AuthContext';
import GuideBuilder from './GuideBuilder';
import ClientOnboardingHub from './ClientOnboardingHub';
import FeatureGuard from './FeatureGuard';
import { Bell, Compass, CreditCard, HardDrive, LogOut, Settings, User } from 'lucide-react';
import Logo_Circle from './ItineraryCreator/ui/Logo_Circle.png';
import { normalizeAppSection, readWorkspaceUrlState, replaceWorkspaceUrl } from './utils/builderUrlState';
import { readBackupSummary } from './backup/localBackup';

// Lazy-loaded: keeps recharts + the dashboards out of the main bundle
const AnalyticsApp = lazy(() => import('./analytics/AnalyticsApp'));
const SettingsApp = lazy(() => import('./settings/SettingsApp'));
const NotificationsApp = lazy(() => import('./notifications/NotificationsApp'));
const SubscriptionsApp = lazy(() => import('./subscriptions/SubscriptionsApp'));
const BackupApp = lazy(() => import('./backup/BackupApp'));

const BACKUP_STALE_DAYS = 7;

// Short badge for the Backup tab: backup age, or how many files are missing from the copy
const getBackupBadge = (summary) => {
  if (!summary?.lastBackupAt) return { text: 'never', stale: true };
  const days = Math.floor((Date.now() - Date.parse(summary.lastBackupAt)) / 864e5);
  const age = days <= 0 ? 'today' : `${days}d ago`;
  const stale = days >= BACKUP_STALE_DAYS || summary.outdatedFiles > 0;
  return { text: summary.outdatedFiles > 0 ? `${age} · ${summary.outdatedFiles} new` : age, stale };
};

// The settings, notifications and subscriptions APIs check the role again
// server-side; this only hides the tabs.
const SETTINGS_ROLES = ['admin', 'superadmin'];

const MainApp = () => {
  const { currentUser, logout, userRole } = useAuth();
  const canManageSettings = SETTINGS_ROLES.includes(String(userRole || '').toLowerCase());
  const [activeSection, setActiveSection] = useState(() => readWorkspaceUrlState().section);
  const [backupSummary, setBackupSummary] = useState(readBackupSummary);
  const backupBadge = getBackupBadge(backupSummary);

  useEffect(() => {
    const refreshBackupSummary = () => setBackupSummary(readBackupSummary());
    window.addEventListener('guidesofia-backup-summary', refreshBackupSummary);
    return () => window.removeEventListener('guidesofia-backup-summary', refreshBackupSummary);
  }, []);

  const openSection = (section) => {
    const normalized = normalizeAppSection(section);
    setActiveSection(normalized);
    replaceWorkspaceUrl({ section: normalized });
  };

  useEffect(() => {
    const restoreSectionFromUrl = () => setActiveSection(readWorkspaceUrlState().section);
    window.addEventListener('popstate', restoreSectionFromUrl);
    return () => window.removeEventListener('popstate', restoreSectionFromUrl);
  }, []);

  const handleLogout = () => {
    logout();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Main Header with Navigation */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Left: Logo and Main Navigation */}
            <div className="flex items-center space-x-8">
              <div className="flex items-center space-x-2.5">
                <img src={Logo_Circle} alt="" className="w-8 h-8" />
                <span className="text-lg font-semibold text-secondary tracking-tight">
                  GuideSofia
                </span>
              </div>

              {/* Main Navigation Tabs */}
              <nav className="flex space-x-1">
                <button
                  onClick={() => openSection('onboarding')}
                  className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                    activeSection === 'onboarding'
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  <Compass className="w-4 h-4" />
                  Onboarding
                </button>
                <button
                  onClick={() => openSection('builder')}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                    activeSection === 'builder'
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  Builder
                </button>
                <FeatureGuard featureName="analytics" requiredAccess="view_only">
                  <button
                    onClick={() => openSection('analytics')}
                    className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                      activeSection === 'analytics'
                        ? 'bg-blue-100 text-blue-700'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                  >
                    Analytics
                  </button>
                </FeatureGuard>
                {canManageSettings && (
                  <button
                    onClick={() => openSection('settings')}
                    className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                      activeSection === 'settings'
                        ? 'bg-blue-100 text-blue-700'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                  >
                    <Settings className="w-4 h-4" />
                    Settings
                  </button>
                )}
                {canManageSettings && (
                  <button
                    onClick={() => openSection('notifications')}
                    className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                      activeSection === 'notifications'
                        ? 'bg-blue-100 text-blue-700'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                  >
                    <Bell className="w-4 h-4" />
                    Notifications
                  </button>
                )}
                {canManageSettings && (
                  <button
                    onClick={() => openSection('subscriptions')}
                    className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                      activeSection === 'subscriptions'
                        ? 'bg-blue-100 text-blue-700'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                  >
                    <CreditCard className="w-4 h-4" />
                    Subscriptions
                  </button>
                )}
                {canManageSettings && (
                  <button
                    onClick={() => openSection('backup')}
                    className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                      activeSection === 'backup'
                        ? 'bg-blue-100 text-blue-700'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                  >
                    <HardDrive className="w-4 h-4" />
                    Backup
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                        backupBadge.stale ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'
                      }`}
                    >
                      {backupBadge.text}
                    </span>
                  </button>
                )}
              </nav>
            </div>

            {/* Right: User Menu */}
            <div className="flex items-center space-x-4">
              <div className="hidden sm:flex items-center space-x-2 text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-full px-3 py-1.5">
                <User className="w-4 h-4 text-gray-400" />
                <span className="max-w-48 truncate">{currentUser?.email}</span>
              </div>

              <button
                onClick={handleLogout}
                className="flex items-center space-x-1 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
              >
                <LogOut className="w-4 h-4" />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1">
        {activeSection === 'onboarding' && (
          <ClientOnboardingHub onOpenBuilder={() => openSection('builder')} />
        )}
        {activeSection === 'builder' && <GuideBuilder />}
        {activeSection === 'analytics' && (
          <FeatureGuard
            featureName="analytics"
            requiredAccess="view_only"
            fallback={
              <div className="flex items-center justify-center h-screen">
                <div className="text-center">
                  <h2 className="text-2xl font-semibold text-gray-700 mb-2">Access Denied</h2>
                  <p className="text-gray-500">You don't have permission to view analytics.</p>
                </div>
              </div>
            }
          >
            <Suspense
              fallback={
                <div className="flex items-center justify-center h-screen bg-slate-900">
                  <p className="text-gray-300">Loading analytics…</p>
                </div>
              }
            >
              <AnalyticsApp />
            </Suspense>
          </FeatureGuard>
        )}
        {activeSection === 'settings' && (
          canManageSettings ? (
            <Suspense fallback={<div className="flex items-center justify-center h-[60vh] text-gray-500">Loading settings…</div>}>
              <SettingsApp />
            </Suspense>
          ) : (
            <div className="flex items-center justify-center h-[60vh] text-center">
              <div>
                <h2 className="text-2xl font-semibold text-gray-700 mb-2">Access Denied</h2>
                <p className="text-gray-500">Settings are only available to admins.</p>
              </div>
            </div>
          )
        )}
        {activeSection === 'notifications' && (
          canManageSettings ? (
            <Suspense fallback={<div className="flex items-center justify-center h-[60vh] text-gray-500">Loading notifications…</div>}>
              <NotificationsApp />
            </Suspense>
          ) : (
            <div className="flex items-center justify-center h-[60vh] text-center">
              <div>
                <h2 className="text-2xl font-semibold text-gray-700 mb-2">Access Denied</h2>
                <p className="text-gray-500">Notifications are only available to admins.</p>
              </div>
            </div>
          )
        )}
        {activeSection === 'subscriptions' && (
          canManageSettings ? (
            <Suspense fallback={<div className="flex items-center justify-center h-[60vh] text-gray-500">Loading subscriptions…</div>}>
              <SubscriptionsApp />
            </Suspense>
          ) : (
            <div className="flex items-center justify-center h-[60vh] text-center">
              <div>
                <h2 className="text-2xl font-semibold text-gray-700 mb-2">Access Denied</h2>
                <p className="text-gray-500">Subscriptions are only available to admins.</p>
              </div>
            </div>
          )
        )}
        {activeSection === 'backup' && (
          canManageSettings ? (
            <Suspense fallback={<div className="flex items-center justify-center h-[60vh] text-gray-500">Loading backup…</div>}>
              <BackupApp />
            </Suspense>
          ) : (
            <div className="flex items-center justify-center h-[60vh] text-center">
              <div>
                <h2 className="text-2xl font-semibold text-gray-700 mb-2">Access Denied</h2>
                <p className="text-gray-500">Backup is only available to admins.</p>
              </div>
            </div>
          )
        )}
      </main>
    </div>
  );
};

export default MainApp;
